import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { Info, ChevronRight, Sparkles } from 'lucide-react';

// Plutchik's Emotion Wheel - Primary emotions with secondary and tertiary variations
const EMOTION_WHEEL = {
  joy: {
    colour: 'hsl(48, 96%, 53%)',
    bgClass: 'bg-yellow-500/20',
    borderClass: 'border-yellow-500',
    secondary: ['serenity', 'ecstasy'],
    tertiary: ['optimism', 'love'],
    combinations: { trust: 'love', anticipation: 'optimism' },
    description: 'A feeling of pleasure and happiness',
    bodyLocation: 'chest, face (smile)',
    needs: ['celebration', 'connection', 'appreciation']
  },
  trust: {
    colour: 'hsl(142, 76%, 36%)',
    bgClass: 'bg-green-500/20',
    borderClass: 'border-green-500',
    secondary: ['acceptance', 'admiration'],
    tertiary: ['love', 'submission'],
    combinations: { joy: 'love', fear: 'submission' },
    description: 'A feeling of confidence and reliability in someone or something',
    bodyLocation: 'relaxed shoulders, open posture',
    needs: ['safety', 'connection', 'support']
  },
  fear: {
    colour: 'hsl(142, 76%, 26%)',
    bgClass: 'bg-emerald-700/20',
    borderClass: 'border-emerald-700',
    secondary: ['apprehension', 'terror'],
    tertiary: ['submission', 'awe'],
    combinations: { trust: 'submission', surprise: 'awe' },
    description: 'An unpleasant emotion caused by threat of danger or pain',
    bodyLocation: 'stomach, chest tightness, legs',
    needs: ['safety', 'reassurance', 'protection']
  },
  surprise: {
    colour: 'hsl(199, 89%, 48%)',
    bgClass: 'bg-cyan-500/20',
    borderClass: 'border-cyan-500',
    secondary: ['distraction', 'amazement'],
    tertiary: ['awe', 'disapproval'],
    combinations: { fear: 'awe', sadness: 'disapproval' },
    description: 'A brief emotional state from an unexpected event',
    bodyLocation: 'widened eyes, raised eyebrows, sharp breath',
    needs: ['understanding', 'time to process', 'information']
  },
  sadness: {
    colour: 'hsl(217, 91%, 60%)',
    bgClass: 'bg-blue-500/20',
    borderClass: 'border-blue-500',
    secondary: ['pensiveness', 'grief'],
    tertiary: ['disapproval', 'remorse'],
    combinations: { surprise: 'disapproval', disgust: 'remorse' },
    description: 'Emotional pain associated with loss or disappointment',
    bodyLocation: 'heaviness in chest, throat tightness, watery eyes',
    needs: ['comfort', 'acknowledgement', 'space to grieve']
  },
  disgust: {
    colour: 'hsl(271, 81%, 56%)',
    bgClass: 'bg-purple-500/20',
    borderClass: 'border-purple-500',
    secondary: ['boredom', 'loathing'],
    tertiary: ['remorse', 'contempt'],
    combinations: { sadness: 'remorse', anger: 'contempt' },
    description: 'A strong feeling of dislike or revulsion',
    bodyLocation: 'nausea, throat, facial tension',
    needs: ['boundaries', 'distance', 'values alignment']
  },
  anger: {
    colour: 'hsl(0, 84%, 60%)',
    bgClass: 'bg-red-500/20',
    borderClass: 'border-red-500',
    secondary: ['annoyance', 'rage'],
    tertiary: ['contempt', 'aggressiveness'],
    combinations: { disgust: 'contempt', anticipation: 'aggressiveness' },
    description: 'A strong feeling of displeasure and hostility',
    bodyLocation: 'jaw, fists, shoulders, heat in face',
    needs: ['respect', 'fairness', 'to be heard']
  },
  anticipation: {
    colour: 'hsl(25, 95%, 53%)',
    bgClass: 'bg-orange-500/20',
    borderClass: 'border-orange-500',
    secondary: ['interest', 'vigilance'],
    tertiary: ['aggressiveness', 'optimism'],
    combinations: { anger: 'aggressiveness', joy: 'optimism' },
    description: 'Excitement or anxiety about a future event',
    bodyLocation: 'chest, stomach butterflies, alert senses',
    needs: ['preparation', 'control', 'engagement']
  }
};

// Extended emotion vocabulary for granularity
const GRANULAR_EMOTIONS = {
  joy: ['happy', 'content', 'pleased', 'cheerful', 'delighted', 'elated', 'euphoric', 'blissful', 'grateful', 'hopeful', 'proud', 'relieved'],
  trust: ['accepting', 'trusting', 'secure', 'valued', 'respected', 'supported', 'understood', 'connected', 'safe', 'appreciated'],
  fear: ['worried', 'anxious', 'scared', 'terrified', 'nervous', 'panicked', 'insecure', 'overwhelmed', 'helpless', 'vulnerable', 'threatened'],
  surprise: ['shocked', 'amazed', 'astonished', 'confused', 'stunned', 'startled', 'bewildered', 'curious', 'intrigued'],
  sadness: ['sad', 'lonely', 'depressed', 'hopeless', 'disappointed', 'hurt', 'grief-stricken', 'melancholic', 'empty', 'rejected', 'abandoned'],
  disgust: ['disgusted', 'repulsed', 'revolted', 'contemptuous', 'judgmental', 'disapproving', 'uncomfortable', 'embarrassed'],
  anger: ['frustrated', 'irritated', 'annoyed', 'resentful', 'furious', 'enraged', 'bitter', 'hostile', 'offended', 'betrayed', 'jealous'],
  anticipation: ['eager', 'excited', 'expectant', 'hopeful', 'nervous', 'restless', 'impatient', 'motivated', 'curious', 'determined']
};

interface EmotionWheelProps {
  selectedEmotion?: string;
  selectedIntensity?: number;
  onEmotionSelect: (emotion: string, primaryCategory: string) => void;
  onIntensityChange?: (intensity: number) => void;
  showBodyMapping?: boolean;
  showNeeds?: boolean;
}

export function EmotionWheel({ 
  selectedEmotion, 
  selectedIntensity = 5,
  onEmotionSelect,
  onIntensityChange,
  showBodyMapping = true,
  showNeeds = true
}: EmotionWheelProps) {
  const [selectedPrimary, setSelectedPrimary] = useState<string | null>(null);
  const [hoveredPrimary, setHoveredPrimary] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'wheel' | 'list'>('wheel');

  const primaryEmotions = Object.keys(EMOTION_WHEEL);
  const activePrimary = selectedPrimary || hoveredPrimary;
  const activeEmotionData = activePrimary ? EMOTION_WHEEL[activePrimary as keyof typeof EMOTION_WHEEL] : null;

  const handlePrimaryClick = (primary: string) => {
    setSelectedPrimary(primary === selectedPrimary ? null : primary);
  };

  const handleGranularSelect = (emotion: string, primary: string) => {
    onEmotionSelect(emotion, primary);
  };

  // Find which primary category the selected emotion belongs to
  const findPrimaryCategory = (emotion: string): string | null => {
    for (const [primary, emotions] of Object.entries(GRANULAR_EMOTIONS)) {
      if (emotions.includes(emotion.toLowerCase())) return primary;
    }
    return null;
  };

  const selectedPrimaryCategory = selectedEmotion ? findPrimaryCategory(selectedEmotion) : null;

  return (
    <div className="space-y-4">
      {/* View Toggle */}
      <div className="flex justify-end gap-2">
        <Button
          variant={viewMode === 'wheel' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setViewMode('wheel')}
        >
          Wheel View
        </Button>
        <Button
          variant={viewMode === 'list' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setViewMode('list')}
        >
          List View
        </Button>
      </div>

      {viewMode === 'wheel' ? (
        <div className="relative">
          {/* Emotion Wheel */}
          <div className="flex justify-center mb-4">
            <div className="relative w-72 h-72">
              {/* Center */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full bg-muted flex items-center justify-center z-10">
                <span className="text-xs text-muted-foreground text-center">
                  {activePrimary ? activePrimary : 'Tap to explore'}
                </span>
              </div>
              
              {/* Primary Emotion Segments */}
              {primaryEmotions.map((emotion, index) => {
                const angle = (index * 360 / 8) - 90;
                const emotionData = EMOTION_WHEEL[emotion as keyof typeof EMOTION_WHEEL];
                const isSelected = selectedPrimary === emotion || selectedPrimaryCategory === emotion;
                const isHovered = hoveredPrimary === emotion;
                
                return (
                  <motion.button
                    key={emotion}
                    className={`absolute w-16 h-16 rounded-full flex items-center justify-center text-xs font-medium transition-all cursor-pointer border-2 ${emotionData.bgClass} ${isSelected || isHovered ? emotionData.borderClass : 'border-transparent'}`}
                    style={{
                      top: `${50 + 40 * Math.sin(angle * Math.PI / 180)}%`,
                      left: `${50 + 40 * Math.cos(angle * Math.PI / 180)}%`,
                      transform: 'translate(-50%, -50%)',
                      boxShadow: isSelected ? `0 0 20px ${emotionData.colour}40` : undefined
                    }}
                    whileHover={{ scale: 1.15 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handlePrimaryClick(emotion)}
                    onMouseEnter={() => setHoveredPrimary(emotion)}
                    onMouseLeave={() => setHoveredPrimary(null)}
                  >
                    <span className="capitalize">{emotion}</span>
                  </motion.button>
                );
              })}
            </div>
          </div>

          {/* Granular Emotions for Selected Primary */}
          <AnimatePresence>
            {selectedPrimary && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-4"
              >
                <Card className={`${activeEmotionData?.bgClass} border-2 ${activeEmotionData?.borderClass}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg capitalize flex items-center gap-2">
                      {selectedPrimary}
                      <ChevronRight className="w-4 h-4" />
                      <span className="text-sm font-normal text-muted-foreground">Choose specific feeling</span>
                    </CardTitle>
                    <CardDescription>{activeEmotionData?.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Granular emotion badges */}
                    <div className="flex flex-wrap gap-2">
                      {GRANULAR_EMOTIONS[selectedPrimary as keyof typeof GRANULAR_EMOTIONS]?.map(emotion => (
                        <Badge
                          key={emotion}
                          variant={selectedEmotion?.toLowerCase() === emotion ? 'default' : 'outline'}
                          className="cursor-pointer transition-all hover:scale-105 capitalize"
                          onClick={() => handleGranularSelect(emotion, selectedPrimary)}
                        >
                          {emotion}
                        </Badge>
                      ))}
                    </div>

                    {/* Body Location */}
                    {showBodyMapping && activeEmotionData?.bodyLocation && (
                      <div className="p-3 rounded-lg bg-background/50 border">
                        <div className="flex items-start gap-2">
                          <Info className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                          <div>
                            <p className="text-sm font-medium">Where you might feel it</p>
                            <p className="text-xs text-muted-foreground">{activeEmotionData.bodyLocation}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Underlying Needs */}
                    {showNeeds && activeEmotionData?.needs && (
                      <div className="p-3 rounded-lg bg-background/50 border">
                        <div className="flex items-start gap-2">
                          <Sparkles className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                          <div>
                            <p className="text-sm font-medium">Possible underlying needs</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {activeEmotionData.needs.map(need => (
                                <Badge key={need} variant="secondary" className="text-xs">
                                  {need}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Emotion Combinations */}
                    {activeEmotionData?.combinations && (
                      <div className="p-3 rounded-lg bg-background/50 border">
                        <p className="text-sm font-medium mb-2">Emotion combinations</p>
                        <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                          {Object.entries(activeEmotionData.combinations).map(([combo, result]) => (
                            <span key={combo}>
                              <span className="capitalize">{selectedPrimary}</span> + <span className="capitalize">{combo}</span> = <span className="font-medium text-foreground capitalize">{result}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ) : (
        /* List View */
        <div className="grid gap-3">
          {primaryEmotions.map(primary => {
            const emotionData = EMOTION_WHEEL[primary as keyof typeof EMOTION_WHEEL];
            const isExpanded = selectedPrimary === primary;
            
            return (
              <Card 
                key={primary} 
                className={`transition-all cursor-pointer ${emotionData.bgClass} hover:border-2 ${isExpanded ? emotionData.borderClass : ''}`}
                onClick={() => handlePrimaryClick(primary)}
              >
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-4 h-4 rounded-full" 
                        style={{ backgroundColor: emotionData.colour }}
                      />
                      <span className="font-medium capitalize">{primary}</span>
                    </div>
                    <ChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                  </div>
                  
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="pt-4 space-y-3">
                          <p className="text-sm text-muted-foreground">{emotionData.description}</p>
                          <div className="flex flex-wrap gap-2">
                            {GRANULAR_EMOTIONS[primary as keyof typeof GRANULAR_EMOTIONS]?.map(emotion => (
                              <Badge
                                key={emotion}
                                variant={selectedEmotion?.toLowerCase() === emotion ? 'default' : 'outline'}
                                className="cursor-pointer transition-all hover:scale-105 capitalize"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleGranularSelect(emotion, primary);
                                }}
                              >
                                {emotion}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Selected Emotion Display */}
      {selectedEmotion && (
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Selected emotion</p>
                <p className="font-semibold capitalize">{selectedEmotion}</p>
              </div>
              {selectedPrimaryCategory && (
                <Badge 
                  className={EMOTION_WHEEL[selectedPrimaryCategory as keyof typeof EMOTION_WHEEL]?.bgClass}
                >
                  {selectedPrimaryCategory}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export { EMOTION_WHEEL, GRANULAR_EMOTIONS };
